import { RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import { CommandCenterCard, EmptyState, InfoBanner, PageHeader, SectionHeading, StatusBadge } from "@/components/design";
import { Button } from "@/components/ui/button";
import { MICROCOPY } from "@/lib/microcopy";
import { PayPeriodBanner } from "./components/pay-period-banner";
import { CrewStatusRing } from "./components/crew-status-ring";
import { DashboardKpiGrid } from "./components/dashboard-kpi-grid";
import { PayPeriodProgress } from "./components/pay-period-progress";
import { QuickActions } from "./components/quick-actions";
import { SiteActivityStrip } from "./components/site-activity-strip";
import { SmartExceptions } from "./components/smart-exceptions";
import { useDashboard } from "./hooks/use-dashboard";
import { greetingForHour } from "./utils";

function mapPayPeriodStatus(
  status: string | undefined,
): "Draft" | "Submitted" | "Approved" | "Locked" | "Exported" | "Exception" {
  switch (status) {
    case "OPEN":
      return "Draft";
    case "IN_REVIEW":
      return "Submitted";
    case "APPROVED":
      return "Approved";
    case "LOCKED":
      return "Locked";
    case "EXPORTED":
      return "Exported";
    default:
      return "Draft";
  }
}

export function DashboardPage() {
  const { data, loading, error, refresh } = useDashboard();
  const hour = new Date().getHours();
  const greeting = greetingForHour(hour);

  if (error && !data) {
    return (
      <div className="space-y-6">
        <PageHeader title="Werkio Command Center" description="Your construction field command center" />
        <EmptyState
          icon={RefreshCw}
          title="Dashboard unavailable"
          description={error}
          actionLabel="Try again"
          onAction={() => void refresh()}
        />
      </div>
    );
  }

  const firstName = data?.greeting.userName.split(" ")[0] ?? "there";

  return (
    <div className="space-y-6 lg:space-y-8">
      <PageHeader
        title={`${greeting}, ${firstName}`}
        description={
          data
            ? `${data.greeting.companyName} · ${data.currentPayPeriod?.label ?? "No open pay period"}`
            : "Loading your command center…"
        }
        badge={
          data?.currentPayPeriod ? (
            <StatusBadge status={mapPayPeriodStatus(data.currentPayPeriod.status)} />
          ) : undefined
        }
        actions={
          <Button variant="outline" size="sm" onClick={() => void refresh()} disabled={loading}>
            <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        }
      />

      <InfoBanner icon={RefreshCw} variant="info" className="hidden sm:flex">
        {MICROCOPY.auditLogSaved} Review exceptions before exporting payroll.
      </InfoBanner>

      {!loading && !data?.currentPayPeriod ? (
        <div className="rounded-xl border border-dashed border-amber-warning/40 bg-amber-50/30 px-4 py-3 text-sm text-amber-900">
          No active pay period found.{" "}
          <Link to="/pay-periods" className="font-medium underline">
            Create a pay period
          </Link>{" "}
          to start logging labor and running payroll.
        </div>
      ) : null}

      <PayPeriodBanner
        payPeriod={data?.currentPayPeriod ?? null}
        asOfDate={data?.asOfDate}
        loading={loading}
      />

      <SectionHeading
        title="Today's snapshot"
        description="Labor hours, costs, and exceptions across your active sites"
      />
      <DashboardKpiGrid
        kpis={data?.kpis ?? null}
        currency={data?.currency ?? "USD"}
        loading={loading}
      />

      <SectionHeading title="Site activity" description="Hours logged today by job site" />
      <SiteActivityStrip sites={data?.siteActivity ?? []} loading={loading} />

      <SectionHeading title="Payroll progress" description="Current pay period workflow" />
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <PayPeriodProgress
            steps={data?.payPeriodProgress ?? []}
            payPeriod={data?.currentPayPeriod ?? null}
            loading={loading}
          />
        </div>
        <QuickActions />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <CrewStatusRing crewStatus={data?.crewStatus ?? null} loading={loading} />
        <CommandCenterCard
          title="Smart exceptions"
          description="Issues flagged before payroll export"
          variant={data && data.kpis.exceptionsNeedingReview > 0 ? "alert" : "success"}
        >
          <SmartExceptions exceptions={data?.smartExceptions ?? []} loading={loading} />
        </CommandCenterCard>
      </div>
    </div>
  );
}
